import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { HighlightedText } from "blog/components/ui.js";
import usePageMeta from "blog/utils/usePageMeta.js";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "blog/config.js";
import { getAuthor } from "blog/data/authors.js";

import HeroMission from "home/sections/HeroMission.js";
import LatestPosts from "blog/components/LatestPosts.js";
import ExplorerParTheme from "home/sections/ExplorerParTheme.js";
import LigneEditoriale from "home/sections/LigneEditoriale.js";
import ActeursCles from "home/sections/ActeursCles.js";
import SeFormer from "home/sections/SeFormer.js";
import Equipe from "home/sections/Equipe.js";
import ContactCta from "home/sections/ContactCta.js";

/**
 * Page d’accueil « IA à Besançon ».
 *
 * Chaque section est un enfant DIRECT d’AnimationRevealPage : elles glissent
 * alternativement depuis la gauche et la droite à leur apparition.
 * Le Header et le Footer sont rendus par App.js, pas ici.
 *
 * Ordre : hero (seul h1) → derniers articles → explorer par catégorie →
 * ligne éditoriale → acteurs clés → se former → équipe → contact.
 *
 * Métadonnées : le hook blog/utils/usePageMeta.js pose title, description, canonical,
 * Open Graph et JSON-LD cohérents avec le positionnement « média local ». Les données
 * structurées de site (Organization + WebSite avec SearchAction) sont déclarées ici,
 * une fois pour toutes : la page d’accueil en est le point d’entrée naturel.
 *
 * Typographie française : dans les chaînes JS, une espace insécable littérale (U+00A0) précède
 * « ? », « : » et borde l’intérieur des guillemets « » ; dans le JSX, on écrit &nbsp;.
 */

const HOME_DESCRIPTION =
  "Actualités, écosystème, formations et ressources sur l’intelligence artificielle, la tech et l’innovation à Besançon. Le repère local, rédigé à Besançon.";

const founder = getAuthor("jimmy");
// Profils publics du fondateur déclarés dans src/blog/data/authors.js (aucun profil au nom du journal à ce jour).
const founderProfiles = founder ? Object.values(founder.links || {}).filter(link => /^https?:\/\//i.test(link)) : [];

/**
 * Identité du site, déclarée une seule fois ici : la page d’accueil est le point d’entrée
 * naturel pour l’Organization et le WebSite. Les autres pages (blog, articles) référencent
 * l’organisation par son nom ; ces deux nœuds portent en plus un @id stable.
 */
const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "IA Besançon",
  alternateName: SITE_NAME,
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: DEFAULT_OG_IMAGE,
    width: 512,
    height: 512,
  },
  image: DEFAULT_OG_IMAGE,
  description:
    "Journal indépendant et à but non lucratif consacré à l’intelligence artificielle, à la tech et à l’innovation dans le bassin de Besançon.",
  inLanguage: "fr-FR",
  areaServed: [
    { "@type": "City", name: "Besançon" },
    { "@type": "AdministrativeArea", name: "Bourgogne-Franche-Comté" },
  ],
  founder: {
    "@type": "Person",
    name: founder ? founder.name : "Jimmy Inthalangsy",
    ...(founder && founder.role ? { jobTitle: founder.role } : {}),
    ...(founderProfiles.length ? { sameAs: founderProfiles } : {}),
  },
};

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  alternateName: "IA Besançon",
  url: SITE_URL,
  description: HOME_DESCRIPTION,
  inLanguage: "fr-FR",
  publisher: { "@id": `${SITE_URL}/#organization` },
  // Recherche interne du blog (?q=…) : permet à Google d’afficher une boîte de recherche du site.
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

const HOME_META = {
  title: "IA, tech et innovation dans le bassin de Besançon",
  description: HOME_DESCRIPTION,
  path: "/",
  jsonLd: [ORGANIZATION_LD, WEBSITE_LD],
};

export default function HomePage() {
  usePageMeta(HOME_META);

  return (
    <AnimationRevealPage>
      <HeroMission />
      <LatestPosts
        subheading="Derniers articles"
        heading={
          <>
            L’actualité IA et tech, <HighlightedText>vue d’ici.</HighlightedText>
          </>
        }
        description={"Les trois derniers articles publiés : actualités, analyses et repères, sources à l’appui."}
        count={3}
        buttonText="Voir tous les articles"
      />
      <ExplorerParTheme />
      <LigneEditoriale />
      <ActeursCles />
      <SeFormer />
      <Equipe />
      <ContactCta />
    </AnimationRevealPage>
  );
}
